import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMixer } from '../context/MixerContext';
import './ProjectDeepDive.css';

const deepDiveData = {
  'wufers': {
    title: 'Wufers E-commerce Platform',
    tagline: 'Enabling direct-to-consumer personalization at scale',
    heroMetric: { value: 'Custom', label: 'Build-Your-Own-Box Feature' },
    
    problem: {
      technical: 'The existing Shopify theme couldn\'t support complex product bundling with variable pricing based on customer selections. Standard Shopify apps were either too rigid or too expensive for the client\'s needs.',
      business: 'Wufers needed to differentiate from competitors by offering personalized gift boxes while maintaining margins. The "Build Your Own Box" concept was central to their value proposition but had no off-the-shelf solution.'
    },
    
    constraints: [
      { icon: '⏱', text: 'Tight launch deadline for holiday season' },
      { icon: '💰', text: 'Budget constraints ruled out enterprise solutions' },
      { icon: '🔄', text: 'Must integrate seamlessly with existing Shopify workflow' },
      { icon: '📱', text: 'Mobile-first experience required (60%+ mobile traffic)' }
    ],
    
    decisions: [
      {
        choice: 'Custom Liquid + JavaScript over Shopify App',
        reasoning: 'Full control over UX, no recurring app fees, and tighter integration with existing theme. Trade-off: More upfront development time.'
      },
      {
        choice: 'Point-based selection system',
        reasoning: 'Simplified pricing logic while giving customers perceived flexibility. Each treat has a point value; boxes have point limits. Makes complex bundling feel intuitive.'
      },
      {
        choice: 'Real-time cart integration',
        reasoning: 'Built custom JS to construct proper cart line items with all variants. Avoided checkout friction while maintaining inventory accuracy.'
      }
    ],
    
    outcome: {
      technical: 'Delivered a performant, maintainable codebase that the client\'s team can extend. The point-based system architecture has been reused for seasonal variations and new product lines.',
      business: 'Launched before Black Friday. The Build Your Own Box feature became the primary driver of repeat purchases and increased average order value by enabling higher-margin personalized bundles.'
    },
    
    technologies: ['Shopify Liquid', 'JavaScript', 'CSS', 'Shopify Cart API']
  },
  
  'rbc-senior': {
    title: 'Enterprise Translation Service',
    tagline: 'On-prem NLP infrastructure for a global workforce',
    heroMetric: { value: '90K+', label: 'RBC Employees Served' },
    
    problem: {
      technical: 'RBC needed a fully on-premise translation solution due to data residency requirements. Cloud-based APIs (Google, AWS) couldn\'t be used for internal communications containing sensitive data.',
      business: 'Cross-team collaboration suffered in a bilingual (EN/FR) organization. Employees were using unsanctioned external tools, creating compliance risks and inconsistent experiences.'
    },
    
    constraints: [
      { icon: '🔒', text: 'Must run entirely on-premise (no cloud APIs)' },
      { icon: '⚡', text: 'Sub-second response times for user experience' },
      { icon: '🏢', text: 'Enterprise security and compliance requirements' },
      { icon: '🔧', text: 'Integration with existing Microsoft ecosystem' }
    ],
    
    decisions: [
      {
        choice: 'Open-source NLP models over commercial licensing',
        reasoning: 'Avoided vendor lock-in and per-seat licensing costs. Enabled customization for domain-specific terminology (banking/finance terms).'
      },
      {
        choice: 'Microservices architecture with dedicated translation nodes',
        reasoning: 'Isolated compute-heavy translation from main application. Enabled horizontal scaling and fault isolation.'
      },
      {
        choice: 'Progressive rollout strategy',
        reasoning: 'Started with internal tools team, expanded to departments, then org-wide. Built confidence and collected feedback before full deployment.'
      }
    ],
    
    outcome: {
      technical: 'Built a scalable, maintainable service that handles thousands of daily translation requests with P99 latency under 500ms. Architecture has become a template for other on-prem ML services.',
      business: 'Eliminated shadow IT translation tools. Improved cross-team collaboration efficiency. Service became a cited example in RBC\'s digital workplace strategy presentations.'
    },
    
    technologies: ['React', 'Node.js', 'Python', 'Spring Boot', 'SQL Server']
  },
  
  'rbc-amplify': {
    title: 'Hybrid Work Coordination Platform',
    tagline: 'From intern project to 80,000-employee rollout',
    heroMetric: { value: '80K+', label: 'Target User Base' },
    
    problem: {
      technical: 'No existing system could handle dynamic team coordination across multiple office locations with real-time occupancy data and employee preferences.',
      business: 'Post-pandemic, RBC faced the "future of work" challenge: how to coordinate hybrid teams effectively without mandating rigid in-office schedules.'
    },
    
    constraints: [
      { icon: '⏱', text: '16-week internship timeline' },
      { icon: '👥', text: 'Team of 4 with varying experience levels' },
      { icon: '📊', text: 'Must present to C-level executives multiple times' },
      { icon: '🔄', text: 'Pivot-ready: requirements evolved based on feedback' }
    ],
    
    decisions: [
      {
        choice: 'React Native for cross-platform mobile-first',
        reasoning: 'Maximum reach with single codebase. Employees needed access from personal devices. Web app for desktop covered remaining use cases.'
      },
      {
        choice: 'Preference-based matching algorithm',
        reasoning: 'Rather than forcing schedules, the system suggests optimal in-office days based on team overlap and individual preferences. Respects autonomy while enabling coordination.'
      },
      {
        choice: 'Continuous executive pitching',
        reasoning: 'Presented progress every 2 weeks to senior leadership. Built stakeholder buy-in throughout development rather than as a final demo.'
      }
    ],
    
    outcome: {
      technical: 'Delivered a production-ready MVP with real-time sync, push notifications, and analytics dashboard. Codebase was clean enough for RBC\'s internal team to continue development.',
      business: 'Won Crowd Favorite and Best Team at AMP Expo. Application was selected for full organizational rollout. Demonstrated that intern teams can deliver enterprise-grade solutions with proper support.'
    },
    
    technologies: ['React Native', 'React', 'PostgreSQL', 'Python', 'Flask']
  },
  
  '519-inventory': {
    title: '519 Inventory System',
    tagline: 'Real-time stock visibility for a growing retail brand',
    heroMetric: { value: 'Real-time', label: 'Inventory Sync' },
    
    problem: {
      technical: 'The client had no visibility into actual stock levels. Inventory was tracked manually in spreadsheets, leading to frequent mismatches between Shopify and physical stock.',
      business: 'Stockouts were causing lost sales and customer frustration. Over-ordering tied up capital in excess inventory. No data to inform reordering decisions.'
    },
    
    constraints: [
      { icon: '💰', text: 'Small business budget' },
      { icon: '🔄', text: 'Must work with existing Shopify setup' },
      { icon: '👤', text: 'Single developer project' },
      { icon: '📱', text: 'Staff needed mobile-friendly interface' }
    ],
    
    decisions: [
      {
        choice: 'Firebase for backend over custom server',
        reasoning: 'Real-time sync out of the box. No server maintenance. Cost-effective at client\'s scale. Trade-off: Less flexibility than custom backend.'
      },
      {
        choice: 'Shopify webhooks for purchase events',
        reasoning: 'Automatic inventory updates post-purchase. No manual reconciliation needed. Single source of truth maintained.'
      },
      {
        choice: 'Custom SKU logic for variants',
        reasoning: 'Client\'s products had complex variant structures (size, color, logo). Built mapping system to accurately track stock at the variant level.'
      }
    ],
    
    outcome: {
      technical: 'Delivered a lightweight, reliable system that syncs inventory within seconds of Shopify events. Firebase hosting keeps costs near zero at current scale.',
      business: 'Eliminated manual inventory tracking. Reorder alerts prevent stockouts. Client has clear data for purchasing decisions. System paid for itself within first month of prevented stockouts.'
    },
    
    technologies: ['HTML', 'JavaScript', 'CSS', 'Firebase', 'Shopify API']
  }
};

export default function ProjectDeepDive({ projectId, onClose }) {
  const { mixerState } = useMixer();
  const data = deepDiveData[projectId];
  
  const techBusinessBlend = mixerState.technicalVsBusiness / 100;
  const showTechnical = techBusinessBlend < 0.5;
  
  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  
  // Close on escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  
  if (!data) return null;
  
  return (
    <AnimatePresence>
      <motion.div 
        className="deep-dive-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div 
          className="deep-dive"
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="deep-dive__close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          
          <div className="deep-dive__content">
            <header className="deep-dive__header">
              <div className="deep-dive__header-text">
                <h2 className="deep-dive__title">{data.title}</h2>
                <p className="deep-dive__tagline">{data.tagline}</p>
              </div>
              <div className="deep-dive__hero-metric">
                <span className="deep-dive__metric-value">{data.heroMetric.value}</span>
                <span className="deep-dive__metric-label">{data.heroMetric.label}</span>
              </div>
            </header>
            
            <section className="deep-dive__section">
              <h3 className="deep-dive__section-title">The Problem</h3>
              <motion.p 
                className="deep-dive__section-text"
                key={showTechnical ? 'tech' : 'biz'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {showTechnical ? data.problem.technical : data.problem.business}
              </motion.p>
              <div className="deep-dive__perspective-indicator">
                <span className={!showTechnical ? 'active' : ''}>Business View</span>
                <span className={showTechnical ? 'active' : ''}>Technical View</span>
              </div>
            </section>
            
            <section className="deep-dive__section">
              <h3 className="deep-dive__section-title">Constraints</h3>
              <div className="deep-dive__constraints">
                {data.constraints.map((constraint, i) => (
                  <motion.div 
                    key={i}
                    className="deep-dive__constraint"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <span className="deep-dive__constraint-icon">{constraint.icon}</span>
                    <span>{constraint.text}</span>
                  </motion.div>
                ))}
              </div>
            </section>
            
            <section className="deep-dive__section">
              <h3 className="deep-dive__section-title">Key Decisions</h3>
              <div className="deep-dive__decisions">
                {data.decisions.map((decision, i) => (
                  <motion.div 
                    key={i}
                    className="deep-dive__decision"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + i * 0.1 }}
                  >
                    <h4 className="deep-dive__decision-choice">{decision.choice}</h4>
                    <p className="deep-dive__decision-reasoning">{decision.reasoning}</p>
                  </motion.div>
                ))}
              </div>
            </section>
            
            <section className="deep-dive__section">
              <h3 className="deep-dive__section-title">Outcome</h3>
              <motion.p 
                className="deep-dive__section-text"
                key={showTechnical ? 'tech-outcome' : 'biz-outcome'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {showTechnical ? data.outcome.technical : data.outcome.business}
              </motion.p>
            </section>
            
            <footer className="deep-dive__footer">
              <div className="deep-dive__technologies">
                {data.technologies.map(tech => (
                  <span key={tech} className="deep-dive__tech">{tech}</span>
                ))}
              </div>
            </footer>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
